import { Component, HostListener, AfterViewInit, OnDestroy, effect } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { MenubarModule } from 'primeng/menubar';
import { TooltipModule } from 'primeng/tooltip';
import { take, Observable, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { UserMenuDropdown } from '@/shared/user-menu-dropdown/user-menu-dropdown';
import { ButtonModule } from 'primeng/button';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SessionData } from '../../../helper/auth.helper';
import { ThemeHelper } from '../../../helper/theme.helper';
import { AuthService } from '../../../services/auth.service';
import { ThemeService } from '../../../services/theme.service';
import { PermissionService } from '../../../services/permission.service';
import { LayoutService } from '../../service/layout.service';

type AppMenuItem = MenuItem & { permission?: string | string[]; items?: AppMenuItem[] };

@Component({
    selector: 'app-management-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, MenubarModule, TooltipModule, TranslateModule, UserMenuDropdown, ButtonModule, ToggleSwitchModule],
    templateUrl: 'app-management.topbar.html'
})
export class AppManagementTopbar implements AfterViewInit, OnDestroy {
    items!: MenuItem[];
    themeData: ThemeHelper | undefined;
    sessionData: SessionData | undefined;
    checked: boolean = false;
    private resizeSubscription?: Subscription;
    private sessionSubscription?: Subscription;

    resize$ = new Observable<number>((subscriber) => {
        const emitWidth = () => {
            subscriber.next(window.innerWidth);
        };

        // Emit initial width
        emitWidth();

        // Create ResizeObserver to watch for viewport changes (including dev tools)
        const resizeObserver = new ResizeObserver(emitWidth);
        resizeObserver.observe(document.body);

        // Also listen to window resize events as fallback
        window.addEventListener('resize', emitWidth);

        // Cleanup
        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', emitWidth);
        };
    });

    @HostListener('window:resize', ['$event.target.innerWidth'])
    toggleUserMenu(width: number) {
        const userDropdown = document.querySelector('.layout-topbar-menu') as HTMLDivElement;
        if (width > 1100) {
            userDropdown.classList.remove('hidden');
        } else {
            userDropdown.classList.add('hidden');
        }
    }

    constructor(
        public layoutService: LayoutService,
        private themeService: ThemeService,
        private authService: AuthService,
        private permissionService: PermissionService
    ) {
        this.resizeSubscription = this.resize$.subscribe(() => {
            this.rebuildMenu();
        });
        this.sessionSubscription = this.authService.sessionData.subscribe({
            next: (data) => {
                this.sessionData = data;
            }
        });

        this.themeService.themeData.subscribe({
            next: (data) => {
                this.themeData = data;
            }
        });

        effect(() => {
            this.permissionService.version();
            this.rebuildMenu();
        });
    }

    private rebuildMenu(): void {
        const width = window.innerWidth;
        const raw = width <= 1100 || width > 1417 ? this.buildMenuItems() : this.buildMenuItemsForTablet();
        this.items = this.filterMenu(raw);
    }

    private filterMenu(items: AppMenuItem[]): MenuItem[] {
        return items
            .filter((item) => !item.permission || this.permissionService.hasPermission(item.permission))
            .map((item) => {
                if (item.items && item.items.length) {
                    return { ...item, items: this.filterMenu(item.items) };
                }
                return { ...item };
            })
            .filter((item) => {
                const isEmptyGroup = Array.isArray(item.items) && item.items.length === 0;
                const hasOwnTarget = !!item.routerLink || !!item.command || !!item.url;
                return !(isEmptyGroup && !hasOwnTarget);
            }) as MenuItem[];
    }

    ngAfterViewInit(): void {
        const userDropdown = document.querySelector('.layout-topbar-menu') as HTMLDivElement;
        if (window.innerWidth > 1100) {
            userDropdown.classList.remove('hidden');
        } else {
            userDropdown.classList.add('hidden');
        }
    }

    ngOnDestroy() {
        this.resizeSubscription?.unsubscribe();
        this.sessionSubscription?.unsubscribe();
    }

    private buildMenuItems(): AppMenuItem[] {
        // Default full menu for non-logistics users
        return [
            {
                label: 'Dashboard',
                icon: 'pi pi-fw pi-home',
                routerLink: '/admin/dashboard'
            },
            {
                label: 'Partnergesellschaften',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: 'Partnergesellschaften',
                        icon: 'pi pi-fw pi-home',
                        routerLink: '/admin/partner-companies/',
                        permission: 'partner-company_read'
                    },
                    {
                        label: 'Dokumententypen',
                        icon: 'pi pi-fw pi-home',
                        routerLink: '/admin/document-types/',
                        permission: 'document-type_read'
                    },
                    {
                        label: 'Ansprechpartner',
                        icon: 'pi pi-fw pi-users',
                        routerLink: '/admin/partner-company-users/',
                        permission: 'partner-company-user_read'
                    },
                ]
            },
            {
                label: 'Benutzer',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: 'Benutzer',
                        icon: 'pi pi-fw pi-users',
                        permission: 'user_read',
                        items: [
                            { label: 'Alle Benutzer', icon: 'pi pi-fw pi-users', routerLink: '/admin/users/', permission: 'user_read' },
                            { label: 'Benutzer erstellen', icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/users/create', permission: 'user_write' }
                        ]
                    },
                    {
                        label: 'Rollen',
                        icon: 'pi pi-fw pi-id-card',
                        permission: 'role_read',
                        items: [
                            { label: 'Alle Rollen', icon: 'pi pi-fw pi-id-card', routerLink: '/admin/roles/', permission: 'role_read' },
                            { label: 'Rolle erstellen', icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/roles/create', permission: 'role_write' }
                        ]
                    },
                    {
                        label: 'Berechtigungen',
                        icon: 'pi pi-fw pi-key',
                        routerLink: '/admin/permissions/',
                        permission: 'permission_read'
                    }
                ]
            },
            {
                label: 'Provisionsabrechnungen',
                icon: 'pi pi-fw pi-euro',
                items: [
                    {
                        label: 'Geöffnete Abrechnungen',
                        icon: 'pi pi-fw pi-eye',
                        routerLink: '/admin/provision-access-logs/',
                        permission: 'provision-access-log_read'
                    }
                ]
            },
            {
                label: 'Sync',
                icon: 'pi pi-fw pi-sync',
                routerLink: '/admin/sync',
                permission: 'partner-company_sync'
            },
            {
                label: 'Einstellungen',
                icon: 'pi pi-fw pi-cog',
                routerLink: '/admin/settings',
                permission: 'setting_read'
            }
        ];
    }

    private buildMenuItemsForTablet(): AppMenuItem[] {
        // Default full menu for non-logistics users
        return [
            {
                label: '',
                icon: 'pi pi-fw pi-home',
                routerLink: '/admin/dashboard'
            },
            {
                label: '',
                icon: 'pi pi-fw pi-users',
                items: [
                    {
                        label: 'Benutzer',
                        icon: 'pi pi-fw pi-users',
                        permission: 'user_read',
                        items: [
                            { label: 'Alle Benutzer', icon: 'pi pi-fw pi-users', routerLink: '/admin/users/', permission: 'user_read' },
                            { label: 'Benutzer erstellen', icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/users/create', permission: 'user_write' }
                        ]
                    },
                    {
                        label: 'Rollen',
                        icon: 'pi pi-fw pi-id-card',
                        permission: 'role_read',
                        items: [
                            { label: 'Alle Rollen', icon: 'pi pi-fw pi-id-card', routerLink: '/admin/roles/', permission: 'role_read' },
                            { label: 'Rolle erstellen', icon: 'pi pi-fw pi-plus-circle', routerLink: '/admin/roles/create', permission: 'role_write' }
                        ]
                    },
                    {
                        label: 'Berechtigungen',
                        icon: 'pi pi-fw pi-key',
                        routerLink: '/admin/permissions/',
                        permission: 'permission_read'
                    }
                ]
            },
            {
                label: '',
                icon: 'pi pi-fw pi-euro',
                items: [
                    {
                        label: 'Geöffnete Abrechnungen',
                        icon: 'pi pi-fw pi-eye',
                        routerLink: '/admin/provision-access-logs/',
                        permission: 'provision-access-log_read'
                    }
                ]
            },
            {
                label: '',
                icon: 'pi pi-fw pi-sync',
                routerLink: '/admin/sync',
                permission: 'partner-company_sync'
            },
            {
                label: '',
                icon: 'pi pi-fw pi-cog',
                routerLink: '/admin/settings',
                permission: 'setting_read'
            }
        ];
    }
}
